/**
 * takeoffPerformanceCalculator.ts
 * Évaluation prudente du risque décollage à CYRC.
 * AVERTISSEMENT : Outil indicatif uniquement — utiliser le POH de l'aéronef.
 */

import { AircraftPerformanceData } from './aircraftPerformanceData';

export interface RunwayOption {
  id: string;
  label: string;
  lengthFt: number;
  lengthM: number;
  heading: number;
}

export const CYRC_RUNWAY_OPTIONS: RunwayOption[] = [
  { id: '06', label: '06', lengthFt: 3733, lengthM: 1138, heading: 40.4 },
  { id: '24', label: '24', lengthFt: 3733, lengthM: 1138, heading: 220.4 },
  { id: '12', label: '12', lengthFt: 6086, lengthM: 1855, heading: 105.5 },
  { id: '30', label: '30', lengthFt: 6086, lengthM: 1855, heading: 285.5 },
  { id: '18', label: '18', lengthFt: 3770, lengthM: 1149, heading: 161.2 },
  { id: '36', label: '36', lengthFt: 3770, lengthM: 1149, heading: 341.2 },
];

export interface WindComponents {
  headwindKt: number;
  tailwindKt: number;
  crosswindKt: number;
}

/**
 * Calcule les composantes vent pour une piste donnée.
 */
export function calculateRunwayWindComponents(
  windDirectionDeg: number,
  windSpeedKt: number,
  runwayHeadingDeg: number
): WindComponents {
  const angleDiff =
    ((windDirectionDeg - runwayHeadingDeg + 360) % 360);
  const angleRad = (angleDiff * Math.PI) / 180;

  const headwind = Math.round(windSpeedKt * Math.cos(angleRad));
  const crosswind = Math.abs(Math.round(windSpeedKt * Math.sin(angleRad)));

  return {
    headwindKt: headwind > 0 ? headwind : 0,
    tailwindKt: headwind < 0 ? Math.abs(headwind) : 0,
    crosswindKt: crosswind,
  };
}

/**
 * Calcule le pourcentage de masse par rapport à la masse max.
 */
export function calculateWeightPercent(
  actualWeightLb: number,
  maxWeightLb: number
): number {
  if (maxWeightLb <= 0) return 0;
  return Math.round((actualWeightLb / maxWeightLb) * 100);
}

export type RiskLevel = 'favorable' | 'verifier' | 'prudence' | 'poh-requis';

export interface RiskLevelInfo {
  level: RiskLevel;
  label: string;
  color: string;
  bg: string;
  border: string;
  icon: string;
}

export function getRiskLevelInfo(level: RiskLevel): RiskLevelInfo {
  switch (level) {
    case 'favorable':
      return { level, label: 'Marge favorable', color: '#16a34a', bg: '#f0fdf4', border: '#86efac', icon: '✓' };
    case 'verifier':
      return { level, label: 'À vérifier', color: '#d97706', bg: '#fffbeb', border: '#fde68a', icon: '⚠' };
    case 'prudence':
      return { level, label: 'Prudence', color: '#ea580c', bg: '#fff7ed', border: '#fdba74', icon: '⚠' };
    case 'poh-requis':
      return { level, label: 'Calcul POH requis', color: '#dc2626', bg: '#fef2f2', border: '#fca5a5', icon: '✕' };
  }
}

export interface TakeoffRiskEvaluation {
  riskLevel: RiskLevel;
  riskInfo: RiskLevelInfo;
  weightPercent: number;
  weightExceeded: boolean;
  runwayAvailableFt: number;
  headwindKt: number;
  tailwindKt: number;
  crosswindKt: number;
  densityAltitudeFt: number;
  densityAltitudeLevel: string;
  summaryMessage: string;
  pohRequired: boolean;
  factors: string[];
}

/**
 * Évalue le risque décollage de façon prudente.
 * Ne produit jamais de distance officielle si les tables POH ne sont pas intégrées.
 */
export function evaluateTakeoffRisk(params: {
  aircraft: AircraftPerformanceData;
  actualWeightLb: number;
  runway: RunwayOption;
  windDirectionDeg: number;
  windSpeedKt: number;
  densityAltitudeFt: number;
  densityAltitudeLevel: string;
}): TakeoffRiskEvaluation {
  const {
    aircraft,
    actualWeightLb,
    runway,
    windDirectionDeg,
    windSpeedKt,
    densityAltitudeFt,
    densityAltitudeLevel,
  } = params;

  const wind = calculateRunwayWindComponents(windDirectionDeg, windSpeedKt, runway.heading);
  const weightPercent = aircraft.maxTakeoffWeightLb > 0
    ? calculateWeightPercent(actualWeightLb, aircraft.maxTakeoffWeightLb)
    : 0;
  const weightExceeded = aircraft.maxTakeoffWeightLb > 0 && actualWeightLb > aircraft.maxTakeoffWeightLb;

  const factors: string[] = [];
  let riskScore = 0; // 0=favorable, 1=verifier, 2=prudence, 3=poh-requis

  // Masse
  if (weightExceeded) {
    riskScore = Math.max(riskScore, 3);
    factors.push('Masse dépasse le maximum de référence');
  } else if (weightPercent > 95) {
    riskScore = Math.max(riskScore, 2);
    factors.push(`Masse élevée (${weightPercent}%)`);
  } else if (weightPercent > 85) {
    riskScore = Math.max(riskScore, 1);
    factors.push(`Masse à surveiller (${weightPercent}%)`);
  }

  // Densité-altitude
  if (densityAltitudeLevel === 'elevee') {
    riskScore = Math.max(riskScore, 3);
    factors.push(`Densité-altitude très élevée (${densityAltitudeFt} ft)`);
  } else if (densityAltitudeLevel === 'prudence') {
    riskScore = Math.max(riskScore, 2);
    factors.push(`Densité-altitude élevée (${densityAltitudeFt} ft)`);
  } else if (densityAltitudeLevel === 'surveiller') {
    riskScore = Math.max(riskScore, 1);
    factors.push(`Densité-altitude à surveiller (${densityAltitudeFt} ft)`);
  }

  // Vent arrière
  if (wind.tailwindKt > 5) {
    riskScore = Math.max(riskScore, 3);
    factors.push(`Vent arrière ${wind.tailwindKt} kt`);
  } else if (wind.tailwindKt > 0) {
    riskScore = Math.max(riskScore, 2);
    factors.push(`Vent arrière ${wind.tailwindKt} kt`);
  }

  // Vent traversier
  if (wind.crosswindKt > 15) {
    riskScore = Math.max(riskScore, 2);
    factors.push(`Vent traversier fort (${wind.crosswindKt} kt)`);
  } else if (wind.crosswindKt > 10) {
    riskScore = Math.max(riskScore, 1);
    factors.push(`Vent traversier (${wind.crosswindKt} kt)`);
  }

  // Piste courte
  if (runway.lengthFt < 2500) {
    riskScore = Math.max(riskScore, 2);
    factors.push(`Piste courte (${runway.lengthFt} ft)`);
  } else if (runway.lengthFt < 4000) {
    riskScore = Math.max(riskScore, 1);
    factors.push(`Piste de longueur modérée (${runway.lengthFt} ft)`);
  }

  // Si POH non disponible, toujours signaler
  if (aircraft.dataStatus === 'POH requis') {
    riskScore = Math.max(riskScore, 2);
    factors.push('Tables POH non intégrées — calcul détaillé indisponible');
  }

  const riskLevelMap: RiskLevel[] = ['favorable', 'verifier', 'prudence', 'poh-requis'];
  const riskLevel = riskLevelMap[Math.min(riskScore, 3)];
  const riskInfo = getRiskLevelInfo(riskLevel);

  // Message de synthèse
  const daPart = `Densité-altitude ${densityAltitudeFt} ft`;
  const massPart = aircraft.maxTakeoffWeightLb > 0 ? `Masse ${weightPercent}%` : 'Masse non vérifiée';
  const windPart = wind.headwindKt > 0
    ? `Vent de face ${wind.headwindKt} kt`
    : wind.tailwindKt > 0
    ? `Vent arrière ${wind.tailwindKt} kt`
    : 'Vent calme';
  const pohPart = aircraft.dataStatus === 'POH requis' ?'Vérifier le POH : performance décollage à calculer.' :'';

  const summaryMessage = [daPart, massPart, windPart, pohPart].filter(Boolean).join('. ');

  return {
    riskLevel,
    riskInfo,
    weightPercent,
    weightExceeded,
    runwayAvailableFt: runway.lengthFt,
    headwindKt: wind.headwindKt,
    tailwindKt: wind.tailwindKt,
    crosswindKt: wind.crosswindKt,
    densityAltitudeFt,
    densityAltitudeLevel,
    summaryMessage,
    pohRequired: aircraft.dataStatus === 'POH requis',
    factors,
  };
}

/**
 * Sélectionne automatiquement la piste recommandée selon le vent.
 */
export function getRecommendedRunway(
  windDirectionDeg: number,
  windSpeedKt: number
): RunwayOption {
  let bestRunway = CYRC_RUNWAY_OPTIONS[0];
  let bestHeadwind = -Infinity;

  for (const runway of CYRC_RUNWAY_OPTIONS) {
    const wind = calculateRunwayWindComponents(windDirectionDeg, windSpeedKt, runway.heading);
    const effective = wind.headwindKt - wind.tailwindKt;
    if (effective > bestHeadwind) {
      bestHeadwind = effective;
      bestRunway = runway;
    }
  }

  return bestRunway;
}
